import type { ChangeEvent, FormEvent, JSX, SyntheticEvent } from 'react'

import { AuthFormBoxRowGaps } from "/app/libraries/arcana/src/blocks/AuthFormBox/index.ts"
import { AuthFormBoxTemplate } from "/app/libraries/arcana/src/blocks/AuthFormBox/Templates/index.ts"
import { InlineBackAngleWithTitle, PrimarySubmitButton } from "/app/libraries/arcana/src/atoms/Button/Templates/index.ts"
import { LoginUsernameInputBlock } from "/app/libraries/arcana/src/blocks/InputBlock/Templates/index.ts"
import { TextBoxCaption3, TextBoxHeadline2 } from "/app/libraries/arcana/src/atoms/TextBox/Templates/index.ts"

import { useForm } from 'effector-forms'
import { useUnit } from 'effector-react'
import { useTranslation } from 'react-i18next'
import { model as router } from '~/core/router'
import { AppTitle } from '~/shared/ui/components/AppTitle'
import { AuthLayout } from '~/widgets/auth'
import { useErrorMessage } from '~/widgets/Popups/MessagePortal'
import { $message, forgotPasswordForm, initResetPasswordFx } from './model'

export function Title(): JSX.Element {
  return <AppTitle t='forgot_password' />
}

ForgotPasswordPage.displayName = 'ForgotPasswordPage'
export function ForgotPasswordPage(): JSX.Element {
  const { t } = useTranslation()

  const pending = useUnit(initResetPasswordFx.pending)
  const message = useUnit($message)

  const { submit, fields, eachValid, hasError, errorText } =
    useForm(forgotPasswordForm)

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    submit()
  }

  const onGoBack = (e: SyntheticEvent) => {
    e.preventDefault()
    e.stopPropagation()
    router.navigateBack()
  }

  useErrorMessage(message, 'ForgotPasswordPage')

  return (
    <AuthLayout>
      <form onSubmit={onSubmit} autoComplete='off' noValidate>
        <AuthFormBoxTemplate rowGap={AuthFormBoxRowGaps.SIZE_20}>
          <a href='/sign-in' onClick={onGoBack}>
            {/* FIXME: button inside anchor is not valid HTML */}
            <InlineBackAngleWithTitle title={t('back')} />
          </a>
          <TextBoxHeadline2 title={t('reset_password')} />
          <TextBoxCaption3 title={t('email_to_reset')} />
          <LoginUsernameInputBlock
            value={fields.email.value}
            placeholder={t('email_placeholder')}
            label={t('email')}
            disabled={pending}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              fields.email.onChange(e.target.value)
            }
            errorMessage={
              hasError('email')
                ? errorText('email', {
                    required: t('email_invalid_error'),
                    email: t('email_invalid_error'),
                    invalid: t('email_invalid_error'),
                  })
                : ''
            }
            autoComplete='off'
          />
          <PrimarySubmitButton
            title={t('verify_continue')}
            disabled={pending || !eachValid}
            withLoader
            isLoading={pending}
          />
        </AuthFormBoxTemplate>
      </form>
    </AuthLayout>
  )
}
